// src/components/LoadingState.js
// src/components/LoadingState.js

import React from 'react';
import {
    Box,
    Typography,
    LinearProgress,
    useTheme,
    Avatar,
    Fade,
    Zoom,
    Stack,
    Paper,
    Skeleton,
    Grid,
    useMediaQuery
} from '@mui/material';
import {
    School as SchoolIcon,
    Psychology as PsychologyIcon,
    MenuBook as MenuBookIcon,
    Analytics as AnalyticsIcon,
    Groups as GroupsIcon,
    Assignment as AssignmentIcon,
    Timeline as TimelineIcon,
    Science as ScienceIcon,
    AutoStories as AutoStoriesIcon,
    Lightbulb as LightbulbIcon,
    ConnectWithoutContact as ConnectIcon,
    Assessment as AssessmentIcon,
    PendingActions as PendingIcon,
    CheckCircle as CheckCircleIcon,
    TrendingUp as TrendingUpIcon,
    Verified as VerifiedIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const LoadingState = ({ 
    message = "Loading...", 
    subMessage = null,
    icon = null,
    fullPage = false,
    size = "medium",
    variant = "default",
    showProgress = true,
    showDots = true,
    showSkeleton = false,
    skeletonType = "card",
    skeletonCount = 3
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    // Get responsive skeleton count
    const getResponsiveSkeletonCount = () => {
        if (isMobile) return Math.min(skeletonCount, 2);
        if (isTablet) return Math.min(skeletonCount, 4);
        return skeletonCount;
    };

    // Size mappings with responsive adjustments
    const sizeMap = {
        small: {
            iconSize: isMobile ? 32 : 40,
            fontSize: isMobile ? 'subtitle1' : 'h6',
            subFontSize: 'body2',
            progressHeight: isMobile ? 3 : 4,
            containerHeight: fullPage ? '100vh' : '200px',
            spacing: isMobile ? 1.5 : 2,
            dotSize: isMobile ? 4 : 6,
            avatarSize: isMobile ? 52 : 60,
            tipPadding: isMobile ? 1.5 : 2,
            tipFontSize: isMobile ? '0.65rem' : '0.75rem'
        },
        medium: {
            iconSize: isMobile ? 48 : 60,
            fontSize: isMobile ? 'h6' : 'h5',
            subFontSize: isMobile ? 'body2' : 'body1',
            progressHeight: isMobile ? 4 : 6,
            containerHeight: fullPage ? '100vh' : '300px',
            spacing: isMobile ? 2 : 3,
            dotSize: isMobile ? 6 : 8,
            avatarSize: isMobile ? 68 : 80,
            tipPadding: isMobile ? 1.5 : 2,
            tipFontSize: isMobile ? '0.7rem' : '0.75rem'
        },
        large: {
            iconSize: isMobile ? 64 : 80,
            fontSize: isMobile ? 'h5' : 'h4',
            subFontSize: isMobile ? 'subtitle1' : 'h6',
            progressHeight: isMobile ? 6 : 8,
            containerHeight: fullPage ? '100vh' : '400px',
            spacing: isMobile ? 2.5 : 4,
            dotSize: isMobile ? 8 : 10,
            avatarSize: isMobile ? 84 : 100,
            tipPadding: isMobile ? 2 : 2.5,
            tipFontSize: isMobile ? '0.75rem' : '0.875rem'
        }
    };

    const sizes = sizeMap[size];

    // Research workflow icons for different variants
    const getIcon = () => {
        if (icon) return icon;
        
        const iconProps = { sx: { fontSize: sizes.iconSize } };
        
        switch(variant) {
            case 'research':
                return <ScienceIcon {...iconProps} />;
            case 'supervision':
                return <GroupsIcon {...iconProps} />;
            case 'publication':
                return <MenuBookIcon {...iconProps} />;
            case 'analytics':
                return <AnalyticsIcon {...iconProps} />;
            case 'matching':
                return <ConnectIcon {...iconProps} />;
            case 'workflow':
                return <TimelineIcon {...iconProps} />;
            case 'ideation':
                return <LightbulbIcon {...iconProps} />;
            case 'assessment':
                return <AssessmentIcon {...iconProps} />;
            case 'pending':
                return <PendingIcon {...iconProps} />;
            case 'verification':
                return <VerifiedIcon {...iconProps} />;
            default:
                return <SchoolIcon {...iconProps} />;
        }
    };

    // Get loading message based on variant
    const getDefaultMessage = () => {
        switch(variant) {
            case 'research':
                return "Loading Research Data...";
            case 'supervision':
                return "Loading Supervision Details...";
            case 'publication':
                return "Preparing Publications...";
            case 'analytics':
                return "Generating Analytics...";
            case 'matching':
                return "Finding Supervisor Matches...";
            case 'workflow':
                return "Loading Workflow...";
            case 'ideation':
                return "Preparing Research Ideas...";
            case 'assessment':
                return "Loading Assessments...";
            case 'verification':
                return "Verifying Credentials...";
            default:
                return message;
        }
    };

    // Get default sub-message
    const getDefaultSubMessage = () => {
        if (subMessage) return subMessage;
        
        const messages = [
            "Preparing your research environment...",
            "Connecting to SJC ScholarFlow...",
            "Loading academic resources...",
            "Setting up your dashboard...",
            "Syncing research data...",
            "Preparing supervision tools..."
        ];
        return messages[Math.floor(Math.random() * messages.length)];
    };

    // Responsive skeleton loaders for different content types
    const renderSkeleton = () => {
        const responsiveCount = getResponsiveSkeletonCount();
        
        switch(skeletonType) {
            case 'card':
                return (
                    <Grid container spacing={isMobile ? 1.5 : 2}>
                        {[...Array(responsiveCount)].map((_, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Paper sx={{ p: isMobile ? 1.5 : 2, borderRadius: 2 }}>
                                    <Skeleton variant="circular" width={isMobile ? 32 : 40} height={isMobile ? 32 : 40} sx={{ mb: 2 }} />
                                    <Skeleton variant="text" width="80%" height={isMobile ? 28 : 32} />
                                    <Skeleton variant="text" width="60%" />
                                    <Skeleton variant="rectangular" height={isMobile ? 80 : 100} sx={{ mt: 2, borderRadius: 1 }} />
                                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                                        <Skeleton variant="circular" width={isMobile ? 20 : 24} height={isMobile ? 20 : 24} />
                                        <Skeleton variant="circular" width={isMobile ? 20 : 24} height={isMobile ? 20 : 24} />
                                        <Skeleton variant="circular" width={isMobile ? 20 : 24} height={isMobile ? 20 : 24} />
                                    </Box>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                );
            
            case 'table':
                return (
                    <Paper sx={{ p: isMobile ? 1.5 : 2, borderRadius: 2, overflowX: 'auto' }}>
                        <Skeleton variant="text" width="30%" height={isMobile ? 32 : 40} sx={{ mb: 2 }} />
                        {[...Array(responsiveCount)].map((_, index) => (
                            <Box key={index} sx={{ display: 'flex', gap: isMobile ? 1 : 2, mb: 2, flexWrap: 'wrap' }}>
                                <Skeleton variant="text" width={isMobile ? "30%" : "20%"} height={isMobile ? 24 : 30} />
                                <Skeleton variant="text" width={isMobile ? "40%" : "40%"} height={isMobile ? 24 : 30} />
                                <Skeleton variant="text" width={isMobile ? "25%" : "20%"} height={isMobile ? 24 : 30} />
                                {!isMobile && <Skeleton variant="text" width="15%" height={30} />}
                            </Box>
                        ))}
                    </Paper>
                );
            
            case 'list':
                return (
                    <Paper sx={{ p: isMobile ? 1.5 : 2, borderRadius: 2 }}>
                        {[...Array(responsiveCount)].map((_, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1.5 : 2, mb: 2 }}>
                                <Skeleton variant="circular" width={isMobile ? 32 : 40} height={isMobile ? 32 : 40} />
                                <Box sx={{ flex: 1 }}>
                                    <Skeleton variant="text" width="60%" height={isMobile ? 20 : 24} />
                                    <Skeleton variant="text" width="40%" height={isMobile ? 16 : 20} />
                                </Box>
                            </Box>
                        ))}
                    </Paper>
                );
            
            case 'dashboard':
                return (
                    <Grid container spacing={isMobile ? 1.5 : 2}>
                        {/* Stats cards */}
                        {[...Array(isMobile ? 2 : 4)].map((_, idx) => (
                            <Grid item xs={6} sm={6} md={3} key={idx}>
                                <Paper sx={{ p: isMobile ? 1.5 : 2 }}>
                                    <Skeleton variant="circular" width={isMobile ? 32 : 40} height={isMobile ? 32 : 40} sx={{ mb: 1 }} />
                                    <Skeleton variant="text" width="70%" height={isMobile ? 28 : 35} />
                                    <Skeleton variant="text" width="50%" />
                                </Paper>
                            </Grid>
                        ))}
                        {/* Chart skeleton */}
                        <Grid item xs={12} md={8}>
                            <Paper sx={{ p: isMobile ? 1.5 : 2 }}>
                                <Skeleton variant="text" width="40%" height={isMobile ? 24 : 30} sx={{ mb: 2 }} />
                                <Skeleton variant="rectangular" height={isMobile ? 200 : 300} sx={{ borderRadius: 1 }} />
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: isMobile ? 1.5 : 2 }}>
                                <Skeleton variant="text" width="60%" height={isMobile ? 24 : 30} sx={{ mb: 2 }} />
                                {[...Array(isMobile ? 3 : 4)].map((_, i) => (
                                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1.5 : 2, mb: 2 }}>
                                        <Skeleton variant="circular" width={isMobile ? 24 : 32} height={isMobile ? 24 : 32} />
                                        <Box sx={{ flex: 1 }}>
                                            <Skeleton variant="text" width="80%" height={isMobile ? 16 : 20} />
                                            <Skeleton variant="text" width="60%" height={isMobile ? 14 : 16} />
                                        </Box>
                                    </Box>
                                ))}
                            </Paper>
                        </Grid>
                    </Grid>
                );
            
            default:
                return null;
        }
    };

    // ScholarFlow specific loading tips
    const loadingTips = [
        "Did you know? SJC ScholarFlow supports 50+ African universities",
        "Tip: Complete your profile to get better supervisor matches",
        "Research shows that regular progress tracking improves completion rates by 40%",
        "SJC ScholarFlow has facilitated over 1,000 research collaborations",
        "Your research journey starts here - every great discovery begins with a question",
        "Connect with researchers across Africa using SJC ScholarFlow",
        "Track your research progress with our intuitive dashboard"
    ];

    const randomTip = loadingTips[Math.floor(Math.random() * loadingTips.length)];

    if (showSkeleton) {
        return (
            <Box
                sx={{
                    width: '100%',
                    minHeight: sizes.containerHeight,
                    p: isMobile ? 2 : 3
                }}
            >
                {renderSkeleton()}
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: sizes.containerHeight,
                width: '100%',
                gap: sizes.spacing,
                p: isMobile ? 2 : 4,
                background: fullPage ? `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)` : 'transparent'
            }}
        >
            <Zoom in={true} style={{ transitionDelay: '200ms' }}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    {/* Animated research icon */}
                    <motion.div
                        animate={{
                            scale: [1, 1.1, 1],
                            rotate: [0, 5, -5, 0]
                        }}
                        transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}
                    >
                        <Avatar
                            sx={{
                                width: sizes.avatarSize,
                                height: sizes.avatarSize,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.secondary.main}20)`,
                                color: theme.palette.primary.main,
                                boxShadow: `0 8px 32px ${theme.palette.primary.main}40`,
                                border: `2px solid ${theme.palette.primary.main}30`
                            }}
                        >
                            {getIcon()}
                        </Avatar>
                    </motion.div>
                    
                    {/* Animated rings - hidden on very small screens */}
                    {!isMobile && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: -10,
                                left: -10,
                                right: -10,
                                bottom: -10,
                                borderRadius: '50%',
                                border: `2px solid ${theme.palette.primary.light}`,
                                animation: 'ripple 1.5s ease-out infinite',
                                '@keyframes ripple': {
                                    '0%': { transform: 'scale(0.8)', opacity: 0.5 },
                                    '100%': { transform: 'scale(1.3)', opacity: 0 },
                                },
                            }}
                        />
                    )}
                </Box>
            </Zoom>
            
            <Fade in={true} timeout={1000}>
                <Box sx={{ textAlign: 'center', maxWidth: { xs: '90%', sm: 500 }, width: '100%' }}>
                    <Typography 
                        variant={sizes.fontSize} 
                        sx={{ 
                            color: theme.palette.text.primary,
                            mb: 1,
                            fontWeight: 600,
                            letterSpacing: '-0.5px',
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontSize: isMobile ? (size === 'small' ? '1rem' : size === 'medium' ? '1.25rem' : '1.5rem') : undefined
                        }}
                    >
                        {getDefaultMessage()}
                    </Typography>
                    
                    <Typography 
                        variant={sizes.subFontSize}
                        sx={{ 
                            color: theme.palette.text.secondary,
                            mb: 3,
                            opacity: 0.8,
                            fontSize: isMobile ? '0.75rem' : undefined
                        }}
                    >
                        {getDefaultSubMessage()}
                    </Typography>
                    
                    {showProgress && (
                        <LinearProgress 
                            sx={{ 
                                height: sizes.progressHeight, 
                                borderRadius: 4,
                                backgroundColor: theme.palette.primary.light + '30',
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 4,
                                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.info.main})`,
                                    animation: 'shimmer 1.5s ease-in-out infinite',
                                    '@keyframes shimmer': {
                                        '0%': { transform: 'translateX(-100%)' },
                                        '100%': { transform: 'translateX(100%)' },
                                    },
                                }
                            }} 
                        />
                    )}
                    
                    {showDots && (
                        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                            {[...Array(3)].map((_, i) => (
                                <motion.div
                                    key={i}
                                    animate={{
                                        y: [0, -10, 0],
                                        opacity: [0.6, 1, 0.6]
                                    }}
                                    transition={{
                                        duration: 1.4,
                                        repeat: Infinity,
                                        delay: i * 0.2,
                                        ease: "easeInOut"
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: sizes.dotSize,
                                            height: sizes.dotSize,
                                            borderRadius: '50%',
                                            backgroundColor: theme.palette.primary.main,
                                            opacity: 0.6
                                        }}
                                    />
                                </motion.div>
                            ))}
                        </Stack>
                    )}

                    {/* Research Tip - responsive design */}
                    <Paper
                        elevation={0}
                        sx={{
                            mt: 3,
                            p: sizes.tipPadding,
                            borderRadius: 2,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}10, ${theme.palette.secondary.main}10)`,
                            border: `1px solid ${theme.palette.primary.main}20`
                        }}
                    >
                        <Stack 
                            direction={{ xs: 'column', sm: 'row' }} 
                            spacing={1} 
                            alignItems="center"
                            justifyContent="center"
                        >
                            <LightbulbIcon sx={{ fontSize: isMobile ? 16 : 20, color: theme.palette.warning.main }} />
                            <Typography variant="caption" sx={{ 
                                color: theme.palette.text.secondary,
                                fontSize: sizes.tipFontSize,
                                textAlign: 'center'
                            }}>
                                {randomTip}
                            </Typography>
                        </Stack>
                    </Paper>
                </Box>
            </Fade>
        </Box>
    );
};

export default LoadingState;
// Basic usage
{/* <LoadingState message="Loading Dashboard..." />

// Full page loading with custom variant
<LoadingState 
    fullPage={true} 
    variant="research" 
    message="Preparing Research Data..."
/>

// With skeleton loader for cards
<LoadingState 
    showSkeleton={true} 
    skeletonType="card" 
    skeletonCount={6}
    variant="supervision"
/>

// Dashboard skeleton
<LoadingState 
    showSkeleton={true} 
    skeletonType="dashboard" 
    variant="analytics"
    size="large"
/>

// Custom message and submessage
<LoadingState 
    message="Finding Your Perfect Supervisor Match"
    subMessage="Analyzing research interests and expertise..."
    variant="matching"
    size="large"
/>

// Table skeleton for data tables
<LoadingState 
    showSkeleton={true}
    skeletonType="table"
    skeletonCount={10}
    variant="workflow"
/>

// Without progress bar
<LoadingState 
    message="Verifying Credentials"
    variant="verification"
    showProgress={false}
    size="small"
/> */}
